import { Context } from '@hotwired/stimulus';
import { emitter } from 'src/emitter';
import { toggleHidden } from 'src/utils/dom-toggle';
import {
  emitPickedOperator,
  EVENT_ESCAPE,
  EVENT_INPUT,
  EVENT_SET_STATE,
  isActionPickOperator,
  isEventInput,
  isEventSetState,
} from './events';
import SearchBarModalController from './modal_controller';
import {
  TOKEN_OPERATOR_ANY_OF,
  TOKEN_OPERATOR_EQUALS,
  TOKEN_OPERATOR_INVALID,
  TOKEN_OPERATOR_NEQUALS,
  TOKEN_TYPE_AFTER,
  TOKEN_TYPE_ALIAS_ASSET_TYPE,
  TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE,
  TOKEN_TYPE_ALIAS_LOCATED_AT,
  TOKEN_TYPE_ALIAS_NAME,
  TOKEN_TYPE_ASSIGNEE,
  TOKEN_TYPE_AUTHOR,
  TOKEN_TYPE_BEFORE,
  TOKEN_TYPE_CONFIDENTIAL,
  TOKEN_TYPE_INVALID,
  TOKEN_TYPE_LABEL,
  TOKEN_TYPE_RANGE,
  TOKEN_TYPE_REL,
  TOKEN_TYPE_REPORTED_AFTER,
  TOKEN_TYPE_REPORTED_BEFORE,
  TOKEN_TYPE_REPORTED_RANGE,
  TOKEN_TYPE_RESOLVED_AFTER,
  TOKEN_TYPE_RESOLVED_BEFORE,
  TOKEN_TYPE_RESOLVED_RANGE,
  TOKEN_TYPE_ROLE,
  TOKEN_TYPE_SITE,
  TOKEN_TYPE_TITLE,
  TokenOperator,
  TokenType,
} from './tokens';

export type PickEvent = Event & {
  params: {
    operator: string;
  };
};

export const STATE_TOKEN_OPERATOR = 'token:operator';
export const STATE_TOKEN_OPERATOR_ACTIVE = 'token:operator:active';

const AVAILABILITY: Record<TokenType, TokenOperator[]> = {
  [TOKEN_TYPE_ASSIGNEE]: [TOKEN_OPERATOR_ANY_OF, TOKEN_OPERATOR_NEQUALS],
  [TOKEN_TYPE_AUTHOR]: [TOKEN_OPERATOR_ANY_OF, TOKEN_OPERATOR_NEQUALS],
  [TOKEN_TYPE_LABEL]: [
    TOKEN_OPERATOR_ANY_OF,
    TOKEN_OPERATOR_EQUALS,
    TOKEN_OPERATOR_NEQUALS,
  ],
  [TOKEN_TYPE_TITLE]: [
    TOKEN_OPERATOR_ANY_OF,
    TOKEN_OPERATOR_EQUALS,
    TOKEN_OPERATOR_NEQUALS,
  ],
  [TOKEN_TYPE_ALIAS_NAME]: [
    TOKEN_OPERATOR_ANY_OF,
    TOKEN_OPERATOR_EQUALS,
    TOKEN_OPERATOR_NEQUALS,
  ],
  [TOKEN_TYPE_SITE]: [TOKEN_OPERATOR_ANY_OF, TOKEN_OPERATOR_NEQUALS],
  [TOKEN_TYPE_ALIAS_LOCATED_AT]: [
    TOKEN_OPERATOR_ANY_OF,
    TOKEN_OPERATOR_NEQUALS,
  ],
  [TOKEN_TYPE_ROLE]: [TOKEN_OPERATOR_ANY_OF], //, TOKEN_OPERATOR_NEQUALS],
  [TOKEN_TYPE_ALIAS_ASSET_TYPE]: [TOKEN_OPERATOR_ANY_OF],
  [TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE]: [TOKEN_OPERATOR_ANY_OF],
  [TOKEN_TYPE_REL]: [TOKEN_OPERATOR_ANY_OF],
  [TOKEN_TYPE_CONFIDENTIAL]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_BEFORE]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_AFTER]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_RANGE]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_REPORTED_BEFORE]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_REPORTED_AFTER]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_REPORTED_RANGE]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_RESOLVED_BEFORE]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_RESOLVED_AFTER]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_RESOLVED_RANGE]: [TOKEN_OPERATOR_EQUALS],
  [TOKEN_TYPE_INVALID]: [],
};

const LABEL_OVERRIDES: Partial<
  Record<TokenType, Partial<Record<TokenOperator, string>>>
> = {
  [TOKEN_TYPE_TITLE]: {
    [TOKEN_OPERATOR_EQUALS]: 'must contain',
    [TOKEN_OPERATOR_NEQUALS]: "doesn't contain",
    [TOKEN_OPERATOR_ANY_OF]: 'contains one of',
  },
  [TOKEN_TYPE_ALIAS_NAME]: {
    [TOKEN_OPERATOR_EQUALS]: 'must contain',
    [TOKEN_OPERATOR_NEQUALS]: "doesn't contain",
    [TOKEN_OPERATOR_ANY_OF]: 'contains one of',
  },
  [TOKEN_TYPE_SITE]: {
    [TOKEN_OPERATOR_EQUALS]: 'matches / has access',
    [TOKEN_OPERATOR_NEQUALS]: 'does not match',
    [TOKEN_OPERATOR_ANY_OF]: 'matches one of',
  },
  [TOKEN_TYPE_ALIAS_LOCATED_AT]: {
    [TOKEN_OPERATOR_EQUALS]: 'matches / has access',
    [TOKEN_OPERATOR_NEQUALS]: 'does not match',
    [TOKEN_OPERATOR_ANY_OF]: 'matches one of',
  },
  [TOKEN_TYPE_ROLE]: {
    [TOKEN_OPERATOR_EQUALS]: 'has',
    [TOKEN_OPERATOR_NEQUALS]: 'does not have',
    [TOKEN_OPERATOR_ANY_OF]: 'has one of',
  },
  [TOKEN_TYPE_ALIAS_EMPLOYEE_ROLE]: {
    [TOKEN_OPERATOR_EQUALS]: 'has',
    [TOKEN_OPERATOR_NEQUALS]: 'does not have',
    [TOKEN_OPERATOR_ANY_OF]: 'has one of',
  },
  [TOKEN_TYPE_LABEL]: {
    [TOKEN_OPERATOR_EQUALS]: 'has',
    [TOKEN_OPERATOR_NEQUALS]: 'does not have',
    [TOKEN_OPERATOR_ANY_OF]: 'has one of',
  },
};

const LABEL_DEFAULTS = {
  [TOKEN_OPERATOR_EQUALS]: 'is',
  [TOKEN_OPERATOR_NEQUALS]: 'is not',
  [TOKEN_OPERATOR_ANY_OF]: 'is one of',
  [TOKEN_OPERATOR_INVALID]: '<invalid>',
};

export default class SearchBarTokenOperatorModalController extends SearchBarModalController {
  private options!: Record<TokenOperator, Element | null>;

  constructor(context: Context) {
    super(context);

    this.onSearchBarStateChange = this.onSearchBarStateChange.bind(this);
    this.onSearchBarInput = this.onSearchBarInput.bind(this);
    this.onSearchBarEscape = this.onSearchBarEscape.bind(this);
  }

  public connect(): void {
    super.connect();

    emitter.on(EVENT_SET_STATE, this.onSearchBarStateChange);
    emitter.on(EVENT_INPUT, this.onSearchBarInput);
    emitter.on(EVENT_ESCAPE, this.onSearchBarEscape);

    this.options = {
      [TOKEN_OPERATOR_EQUALS]: this.element.querySelector(
        `[data-operator="${TOKEN_OPERATOR_EQUALS}"]`,
      ),
      [TOKEN_OPERATOR_ANY_OF]: this.element.querySelector(
        `[data-operator="${TOKEN_OPERATOR_ANY_OF}"]`,
      ),
      [TOKEN_OPERATOR_NEQUALS]: this.element.querySelector(
        `[data-operator="${TOKEN_OPERATOR_NEQUALS}"]`,
      ),
      [TOKEN_OPERATOR_INVALID]: null,
    };
  }

  public disconnect(): void {
    super.disconnect();

    emitter.off(EVENT_SET_STATE, this.onSearchBarStateChange);
    emitter.off(EVENT_INPUT, this.onSearchBarInput);
    emitter.off(EVENT_ESCAPE, this.onSearchBarEscape);
  }

  private onSearchBarStateChange(event: unknown) {
    if (!isEventSetState(event)) {
      return;
    }

    switch (event.state) {
      case STATE_TOKEN_OPERATOR: {
        this.setAvailability(event.type);

        this.activeItem = null;
        this.active = true;
        break;
      }

      case STATE_TOKEN_OPERATOR_ACTIVE: {
        this.setAvailability(event.type);

        if (!this.activeItem) {
          this.activateFirst();
        }
        this.active = true;
        break;
      }

      default: {
        this.activeItem = null;
        this.active = false;
      }
    }
  }

  private setAvailability(type: TokenType) {
    const typeAvailable = AVAILABILITY[type];
    const labels = LABEL_OVERRIDES[type] || {};
    const fallback = LABEL_DEFAULTS;
    const current = this.activeDescendant;

    for (const optionKey in this.options) {
      const operator = optionKey as TokenOperator;
      const element = this.options[operator];
      if (element) {
        toggleHidden(element, !typeAvailable.includes(operator));

        const label = element.querySelector('[data-target="label"]');
        if (label) {
          label.textContent = labels[operator] ?? fallback[operator];
        }
      }
    }

    // Move active if item availability changes
    if (current && current !== this.activeItem) {
      this.activateFirst();
    }
  }

  private onSearchBarEscape(_: unknown) {
    if (!this.active) {
      return;
    }

    // noop
  }

  private onSearchBarInput(event: unknown) {
    if (!this.active || !isEventInput(event)) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        this.activateNext();
        break;
      }

      case 'ArrowUp': {
        this.activatePrevious();
        break;
      }

      case 'Home': {
        this.activateFirst();
        break;
      }

      case 'End': {
        this.activateLast();
        break;
      }

      case 'Enter': {
        if (this.activeItem) {
          const operator = this.activeItem.getAttribute(
            'data-search-bar--token-operator-modal-operator-param',
          );
          if (operator) {
            this.pick({ params: { operator } });
          }
        }

        break;
      }
    }
  }

  public pick(event: unknown) {
    if (!isActionPickOperator(event)) {
      return;
    }

    emitPickedOperator(event.params.operator);
  }
}
